/**
 * 合約管理 API 服務
 * 使用 RTK Query 封裝合約相關的 API 調用
 * 
 * 注意：目前使用 mock 數據進行開發測試
 * 待 API 整合後，將 mockBaseQuery 替換為 baseQuery
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from '@/services/baseApi';
import { IApiResponse, IPaginatedResponse } from '@/types/api';
import {
  IContract,
  IContractListParams,
  IContractFormData,
  IBatchUpdateContractsRequest,
  IBatchUpdateContractsResponse,
  IContractStatistics,
  ContractStatus,
  IContractFile,
  IContractFileListParams,
  IContractFileBatchUploadResult,
} from '@/types/contract';
import { ConstantUtil } from '@/utils/ConstantUtil';

/**
 * 合約 API 服務
 */
export const contractApi = createApi({
  reducerPath: 'contractApi',
  baseQuery: baseQueryWithErrorHandling,
  // baseQuery, // 真實 API baseQuery（待 API 整合後使用）
  tagTypes: ['Contract', 'ContractStatistics', 'ContractFile'],
  endpoints: (builder) => ({
    // 獲取合約列表
    getContracts: builder.query<IApiResponse<IPaginatedResponse<IContract>>, IContractListParams>({
      query: (params) => ({
        url: ConstantUtil.API_CONTRACTS,
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.searchKeyword && { searchKeyword: params.searchKeyword }),
          ...(params.status && params.status !== 'all' && { status: params.status }),
          ...(params.contractType && params.contractType !== 'all' && { contractType: params.contractType }),
          ...(params.brandId && { brandId: params.brandId }),
          ...(params.contractLevel && params.contractLevel !== 'all' && { contractLevel: params.contractLevel }),
          ...(params.sortBy && { sortBy: params.sortBy }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
        },
      }),
      providesTags: ['Contract'],
    }),

    // 獲取合約詳情
    getContractById: builder.query<IApiResponse<IContract>, string>({
      query: (id) => ({
        url: ConstantUtil.API_CONTRACT_BY_ID(id),
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'Contract', id }],
    }),

    // 創建合約
    createContract: builder.mutation<IApiResponse<IContract>, IContractFormData>({
      query: (data) => ({
        url: ConstantUtil.API_CONTRACT_CREATE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Contract', 'ContractStatistics'],
    }),

    // 更新合約
    updateContract: builder.mutation<IApiResponse<IContract>, { id: string; data: Partial<IContractFormData> }>({
      query: ({ id, data }) => ({
        url: ConstantUtil.API_CONTRACT_UPDATE(id),
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Contract', id }, 'Contract', 'ContractStatistics'],
    }),

    // 刪除合約
    deleteContract: builder.mutation<IApiResponse<void>, string>({
      query: (id) => ({
        url: ConstantUtil.API_CONTRACT_DELETE(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Contract', 'ContractStatistics'],
    }),

    // 批量操作合約
    batchUpdateContracts: builder.mutation<IApiResponse<IBatchUpdateContractsResponse>, IBatchUpdateContractsRequest>({
      query: (data) => ({
        url: ConstantUtil.API_CONTRACT_BATCH_UPDATE,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Contract', 'ContractStatistics'],
    }),

    // 獲取合約統計數據
    getContractStatistics: builder.query<IApiResponse<IContractStatistics>, void>({
      query: () => ({
        url: ConstantUtil.API_CONTRACT_STATISTICS,
        method: 'GET',
      }),
      providesTags: ['ContractStatistics'],
    }),

    // 切換合約狀態
    toggleContractStatus: builder.mutation<IApiResponse<IContract>, { id: string; status: ContractStatus }>({
      query: ({ id, status }) => ({
        url: ConstantUtil.API_CONTRACT_UPDATE(id),
        method: 'PUT',
        body: { status },
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Contract', id }, 'Contract', 'ContractStatistics'],
    }),

    // 上傳合約檔案（單檔）
    uploadContractFile: builder.mutation<
      IApiResponse<IContractFile>,
      { contractId: string; file: File; description?: string; category?: string }
    >({
      query: ({ contractId, file, description, category }) => {
        const formData = new FormData();
        formData.append('file', file);
        if (description) formData.append('description', description);
        if (category) formData.append('category', category);

        return {
          url: ConstantUtil.API_CONTRACT_FILE_UPLOAD(contractId),
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: 'ContractFile', id: contractId },
      ],
    }),

    // 批量上傳合約檔案
    batchUploadContractFiles: builder.mutation<
      IApiResponse<IContractFileBatchUploadResult>,
      { contractId: string; files: File[]; category?: string }
    >({
      query: ({ contractId, files, category }) => {
        const formData = new FormData();
        files.forEach((file) => {
          formData.append('files', file);
        });
        if (category) formData.append('category', category);

        return {
          url: ConstantUtil.API_CONTRACT_FILE_BATCH_UPLOAD(contractId),
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: 'ContractFile', id: contractId },
      ],
    }),

    // 獲取合約檔案列表
    getContractFiles: builder.query<
      IApiResponse<IPaginatedResponse<IContractFile>>,
      { contractId: string } & IContractFileListParams
    >({
      query: ({ contractId, ...params }) => ({
        url: ConstantUtil.API_CONTRACT_FILES(contractId),
        method: 'GET',
        params: {
          page: params.page ?? 1,
          pageSize: params.pageSize ?? ConstantUtil.PAGE_SIZE,
          ...(params.category && { category: params.category }),
        },
      }),
      providesTags: (_result, _error, { contractId }) => [
        { type: 'ContractFile', id: contractId },
      ],
    }),

    // 下載合約檔案
    downloadContractFile: builder.query<
      IApiResponse<{ downloadUrl: string; expiresAt: string }>,
      { contractId: string; fileId: string }
    >({
      query: ({ contractId, fileId }) => ({
        url: ConstantUtil.API_CONTRACT_FILE_DOWNLOAD(contractId, fileId),
        method: 'GET',
      }),
    }),

    // 刪除合約檔案
    deleteContractFile: builder.mutation<
      IApiResponse<void>,
      { contractId: string; fileId: string }
    >({
      query: ({ contractId, fileId }) => ({
        url: ConstantUtil.API_CONTRACT_FILE_DELETE(contractId, fileId),
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { contractId }) => [
        { type: 'ContractFile', id: contractId },
      ],
    }),
  }),
});

// 導出自動生成的 hooks
export const {
  useGetContractsQuery,
  useGetContractByIdQuery,
  useCreateContractMutation,
  useUpdateContractMutation,
  useDeleteContractMutation,
  useBatchUpdateContractsMutation,
  useGetContractStatisticsQuery,
  useToggleContractStatusMutation,
  useUploadContractFileMutation,
  useBatchUploadContractFilesMutation,
  useGetContractFilesQuery,
  useLazyDownloadContractFileQuery,
  useDeleteContractFileMutation,
} = contractApi;

